// The console's clock — every user-facing timestamp formats through here.
//
// Two zones matter in "local" mode, and neither is an accident:
//
//  - TIMELINE surfaces (chart axes and tooltips, the ruler chip, the transport
//    readout, weather-window chips, the briefing prose) read in the SITE's zone —
//    the stated convention from time/cursor.ts: the timeline reads in the clock
//    of the water being worked.
//  - EVENT stamps (when a forecast was retrieved, when feedback was sent) are
//    things that happened on THIS machine, so they read in the browser's zone.
//
// The UTC toggle collapses both families onto UTC and says so in the rendered
// text, because a crew coordinating with shore (or with a met office) wants one
// unambiguous clock. The mode changes FORMATTING ONLY — label parsing, cursor
// instants and every computation stay exactly where they were.
//
// The mode itself is a preference (ui/prefs.ts): localStorage always, mirrored
// to users.prefs when signed in. Surfaces that cache rendered text subscribe via
// subscribeTimeMode and re-render on a flip; canvas surfaces just redraw.

import { timeOfLabel, zonedParts } from "../time/cursor.js";
import { getPref, setPref, subscribePref } from "./prefs.js";

export type TimeMode = "local" | "utc";

/** The prefs key. A sibling preference (units, …) copies this pattern: one key,
 *  a narrowing getter, a setter, and a subscribe that forwards the store's. */
const PREF_KEY = "clock";

/** The active mode. Anything unrecognised in the pref blob is "local" — the
 *  default, and the only safe reading of a value a newer client wrote. */
export function getTimeMode(): TimeMode {
  return getPref(PREF_KEY) === "utc" ? "utc" : "local";
}

export function setTimeMode(mode: TimeMode): void {
  setPref(PREF_KEY, mode);
}

/** Subscribe to mode flips. Returns the unsubscribe. */
export function subscribeTimeMode(fn: () => void): () => void {
  return subscribePref(PREF_KEY, () => fn());
}

// ---------------------------------------------------------------------------
// Formatting. All functions take an INSTANT (epoch ms) — callers with a chart
// label convert via timeOfLabel first, so parsing stays in time/cursor.ts.

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export interface ClockParts {
  year: number;
  month: number; // 1-12
  weekday: number; // 0 = Sunday, as Date#getDay
  day: number;
  hour: number;
  minute: number;
}

function utcParts(t: number): ClockParts {
  const d = new Date(t);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    weekday: d.getUTCDay(),
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  };
}

/** Timeline parts: the site's wall clock, or UTC when toggled. */
function siteParts(t: number): ClockParts {
  return getTimeMode() === "utc" ? utcParts(t) : zonedParts(t);
}

/** Event-stamp parts: this browser's wall clock, or UTC when toggled. */
function browserParts(t: number): ClockParts {
  if (getTimeMode() === "utc") return utcParts(t);
  const d = new Date(t);
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    weekday: d.getDay(),
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

const pad = (n: number): string => String(n).padStart(2, "0");

/** The visible UTC label the toggle promises. Local mode adds nothing: site
 *  time is the console's stated default convention, not a surprise to flag. */
const suffix = (): string => (getTimeMode() === "utc" ? " UTC" : "");

/** "Thu 08:00" / "Thu 08:00 UTC" — the ruler's playhead chip, the briefing's
 *  Start readout. Weekday without date: these sit against a labelled axis. */
export function fmtDayHM(t: number): string {
  const p = siteParts(t);
  return `${WEEKDAYS[p.weekday]} ${pad(p.hour)}:${pad(p.minute)}${suffix()}`;
}

/** "Thu 14 · 08:00" / "Thu 14 · 08:00 UTC" — the transport readout and every
 *  chart tooltip title. */
export function fmtDayDateHM(t: number): string {
  const p = siteParts(t);
  return `${WEEKDAYS[p.weekday]} ${p.day} · ${pad(p.hour)}:${pad(p.minute)}${suffix()}`;
}

/** "Thu 8 AM" / "Thu 8 AM UTC" — the weather-window chips and the briefing
 *  prose, which speak in hours a person says out loud. */
export function fmtDayAmPm(t: number): string {
  const p = siteParts(t);
  const h = p.hour;
  const ampm = h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`;
  return `${WEEKDAYS[p.weekday]} ${ampm}${suffix()}`;
}

/** Axis major tick for a chart label: "Thu 14" at the display zone's midnight,
 *  null elsewhere. In UTC mode midnights move to the labels that are 00:00 UTC,
 *  so the date strip stays truthful under the toggle. Date-only — the ruler
 *  chip and tooltips beside it carry the visible UTC label. */
export function fmtTickLabel(label: string): string | null {
  const t = timeOfLabel(label);
  if (!Number.isFinite(t)) return null;
  const p = siteParts(t);
  if (p.hour !== 0 || p.minute !== 0) return null;
  return `${WEEKDAYS[p.weekday]} ${p.day}`;
}

/** "08:32" / "08:32 UTC" — a bare clock time for event stamps (the forecast
 *  freshness line). Browser zone, not site zone: it answers "when did this
 *  happen here", not "when on the water". */
export function fmtClockHM(t: number): string {
  const p = browserParts(t);
  return `${pad(p.hour)}:${pad(p.minute)}${suffix()}`;
}

/** "Thu 14 08:32" / "Thu 14 08:32 UTC" — a dated event stamp (feedback rows). */
export function fmtEventStamp(t: number): string {
  const p = browserParts(t);
  return `${WEEKDAYS[p.weekday]} ${p.day} ${pad(p.hour)}:${pad(p.minute)}${suffix()}`;
}

// ---------------------------------------------------------------------------
// The conditions table's column axis. It does not format an instant into a
// string and print it — it BUCKETS instants into three-hourly columns and
// groups those columns under day headings, so it needs the parts, not the
// prose. Exposing siteParts under a name that says which clock it speaks keeps
// that bucketing on the console's one stated convention rather than on a second
// reading of the same labels.

/** An instant's wall-clock parts on the TIMELINE clock (site zone, or UTC when
 *  toggled) — what the table groups days and picks 3-hourly columns by. */
export function displayParts(t: number): ClockParts {
  return siteParts(t);
}

/** "Thursday 13" — the table's day-group heading. Long weekday because the
 *  heading spans eight columns and has the room; the hour cells under it are
 *  bare two-digit numbers, so the day has to carry the date. */
export function fmtDayLong(t: number): string {
  const p = displayParts(t);
  return `${WEEKDAYS_LONG[p.weekday]} ${p.day}`;
}

/** "03" — one 3-hourly column head. The UTC/Local label rides on the table's
 *  axis caption once, not on every column. */
export function fmtHourCol(t: number): string {
  return pad(displayParts(t).hour);
}

/** The visible clock label for the table's axis caption: "local" names the
 *  site's zone, the console's stated timeline convention. */
export function clockLabel(): string {
  return getTimeMode() === "utc" ? "UTC" : "site local";
}
